import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { TextV2Props } from '@withjoy/joykit';
import { Box, ButtonV2, IconV2, LinkV2, NoSsr, useDisclosure } from '@withjoy/joykit';
import { NoteText, StyledLinkify, NoteTextWrapper } from './CoupleHeader.styles';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import globalWindow from '@shared/core/globals';
import { useRegistryGuestTranslations } from '../../GuestRegistry.i18n';
import { useTranslation } from '@shared/core';
import { PageNoteDialog } from '../PageNoteDialog';
import { Edit, Note } from '@withjoy/joykit/icons';
import { IconCard } from '@apps/registry/common/components/EventPreview/IconCard';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { useMediaQuery } from '@withjoy/joykit/utils';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { Category } from '@graphql/generated-base';
import { body3ToParagraphOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonPropsBasedOnBackground, getHoverColor } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { isRichTextContent } from '@shared/utils/html';
import { LazyRichText } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichText.lazy';
import { RichTextStylesProvider } from '@apps/guest/packages/layout-engine/widgets/Custom/components/RichText/RichTextStylesProvider';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

// Covers NoteTextWrapper's 0.3s max-height transition, during which a long note measures as fitting.
const COLLAPSE_SETTLE_MS = 400;

type PageNoteTextProps = {
  noteText: Maybe<string>;
  eventHandle: string;
  pageId?: string;
  onClickReadMore?: () => void;
  onEditPageNote?: () => void;
  showEdit: boolean;
  showEmptyCard?: boolean;
  isCustomStylesEnabled: boolean;
};

export const PageNoteText: React.FC<PageNoteTextProps & TextV2Props> = ({
  noteText = '',
  onClickReadMore,
  onEditPageNote,
  eventHandle,
  pageId,
  showEdit = false,
  showEmptyCard = false,
  isCustomStylesEnabled,
  ...textProps
}) => {
  const noteRef = useRef<HTMLDivElement>(null);
  const noteContentRef = useRef<HTMLElement>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isExpanded, setIsExpand] = useState(false);
  // Ref mirror of isExpanded — the ResizeObserver callback ([] deps) would
  // otherwise capture the initial value and never see subsequent toggles.
  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;
  const isSettlingRef = useRef(false);
  const settleTimeoutRef = useRef<ReturnType<typeof globalWindow.setTimeout> | undefined>(undefined);

  const { isOpen: isPageNoteDialogOpen, onOpen: openPageNoteDialog, onClose: closePageNoteDialog } = useDisclosure();

  const [, setHasMountedOnce] = useState(false);

  const { tq } = useTranslation('guestRegistry');
  const { getCoupleHeaderTranslations } = useRegistryGuestTranslations();
  const { readMore, readLess, noteButton, addNoteButton } = getCoupleHeaderTranslations();
  const { pageColor, accentColor } = useColorPaletteProvider();
  const customEditNoteButtonProps = useMemo(
    () => (pageColor && isCustomStylesEnabled ? { ...getButtonPropsBasedOnBackground(pageColor) } : {}),
    [pageColor, isCustomStylesEnabled],
  );
  const primaryTextColor = useMemo(() => (isCustomStylesEnabled ? accentColor : undefined), [accentColor, isCustomStylesEnabled]);
  const hoverColor = useMemo(() => (primaryTextColor ? getHoverColor(primaryTextColor) : undefined), [primaryTextColor]);
  const expandText = isExpanded ? readLess : readMore;
  const emptyNote = !noteText?.length;

  const isMobile = useMediaQuery(theme => theme.mediaQueries.down({ breakpointAlias: 'sm4' }));

  const noteTextTypographyOverride: OverrideTypography = useMemo(() => [{ variant: isMobile ? 'body1' : 'body3', category: Category.PARAGRAPH }], [isMobile]);

  const isMounted = useIsMounted();
  useEffect(() => {
    let timeout: ReturnType<typeof globalWindow.setTimeout>;
    if (isMounted) {
      timeout = globalWindow.setTimeout(() => {
        setHasMountedOnce(true);
      }, 1000);
    }
    return () => {
      globalWindow.clearTimeout(timeout);
    };
  }, [isMounted]);

  useEffect(() => () => globalWindow.clearTimeout(settleTimeoutRef.current), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useIsomorphicLayoutEffect(() => {
    if (noteRef.current) {
      const overflows = noteRef.current.offsetHeight < noteRef.current.scrollHeight;
      if (!isExpanded && !isSettlingRef.current) {
        setHasMore(overflows);
      }
    }
  });

  // Re-measure when the inner content resizes — `LazyRichText` loads its chunk asynchronously,
  // so the initial layout-effect measurement can run before rich-text content is rendered.
  // `transitionend` is the authoritative "wrapper has stopped animating" signal, and replaces
  // any resize dropped while it was still in flight.
  useEffect(() => {
    const wrapperEl = noteRef.current;
    const contentEl = noteContentRef.current;
    if (!wrapperEl || !contentEl) return;

    // Refs, not state — this effect would capture stale values otherwise.
    const measureIfCollapsed = () => {
      if (isExpandedRef.current) return;
      setHasMore(wrapperEl.offsetHeight < wrapperEl.scrollHeight);
    };

    const onResize = () => {
      if (!isSettlingRef.current) measureIfCollapsed();
    };

    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== wrapperEl || event.propertyName !== 'max-height') return;
      isSettlingRef.current = false;
      measureIfCollapsed();
    };

    const observer = createSafeResizeObserver(onResize);
    observer.observe(contentEl);
    wrapperEl.addEventListener('transitionend', onTransitionEnd);
    return () => {
      observer.disconnect();
      wrapperEl.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [emptyNote, noteText]);

  const onClickEdit = useCallback(() => {
    openPageNoteDialog();
  }, [openPageNoteDialog]);

  const onToggleExpand = useCallback(() => {
    isSettlingRef.current = true;
    globalWindow.clearTimeout(settleTimeoutRef.current);
    // Fallback for reduced motion, where no transition runs and transitionend never fires.
    // A transition that does complete fires afterwards and overwrites this measurement.
    settleTimeoutRef.current = globalWindow.setTimeout(() => {
      isSettlingRef.current = false;
      if (!isExpandedRef.current && noteRef.current) {
        setHasMore(noteRef.current.offsetHeight < noteRef.current.scrollHeight);
      }
    }, COLLAPSE_SETTLE_MS);
    setIsExpand(prev => !prev);
    onClickReadMore?.();
  }, [onClickReadMore]);

  const isRichText = !!(noteText && isRichTextContent(noteText));

  // Plain-text fallback used both as SSR output and for legacy non-HTML notes.
  const plainNoteContent = (
    <StyledLinkify
      primaryTextColor={primaryTextColor}
      hoverColor={hoverColor}
    >
      {noteText}
    </StyledLinkify>
  );

  // Always render as `<div>` — `isRichTextContent` is DOM-only and returns false on the server,
  // so a conditional `tagName` would switch from `<p>` (SSR) to `<div>` (client) and trigger a
  // hydration mismatch. Static `<div>` also lets rich text emit block-level elements (`<p>`,
  // `<h1>`, …) without violating `<p>` nesting rules. Matches the Brannan/Aloha pattern.
  const noteContent = (
    <NoteText
      {...textProps}
      ref={noteContentRef}
      tagName="div"
      typographyVariant={isMobile ? 'body1' : 'body3'}
      isOpen={isExpanded}
    >
      <NoSsr fallback={plainNoteContent}>{isRichText ? <LazyRichText richTextComponentData={{ id: pageId ?? '', text: noteText }} /> : plainNoteContent}</NoSsr>
    </NoteText>
  );

  return (
    <>
      {emptyNote && showEmptyCard ? (
        <IconCard
          text={tq`Write a welcome note`}
          icon={<Note />}
          onClick={onClickEdit}
        />
      ) : (
        <>
          <Box>
            <GuestSiteTypographyOverride override={noteTextTypographyOverride}>
              <NoteTextWrapper
                isOpen={isExpanded}
                ref={noteRef}
              >
                {isRichText ? <RichTextStylesProvider>{noteContent}</RichTextStylesProvider> : noteContent}
              </NoteTextWrapper>
            </GuestSiteTypographyOverride>
            {hasMore && (
              <Box
                marginTop={7}
                textAlign="left"
              >
                <GuestSiteTypographyOverride override={body3ToParagraphOverride}>
                  <LinkV2
                    typographyVariant="body3"
                    color={primaryTextColor ?? 'mono14'}
                    _hover={{
                      color: hoverColor,
                    }}
                    textDecoration="underline"
                    onClick={onToggleExpand}
                  >
                    {expandText}
                  </LinkV2>
                </GuestSiteTypographyOverride>
              </Box>
            )}
          </Box>
          {showEdit && (
            <ButtonV2
              typographyVariant="button2"
              intent="neutral"
              variant="outline"
              onClick={onClickEdit}
              marginTop={7}
              borderRadius="1.5rem"
              {...customEditNoteButtonProps}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Box
                  marginRight={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <IconV2 size="sm">
                    <Edit />
                  </IconV2>
                </Box>
                {!emptyNote ? noteButton : addNoteButton}
              </Box>
            </ButtonV2>
          )}
        </>
      )}
      <PageNoteDialog
        onEditPageNote={onEditPageNote}
        isOpen={isPageNoteDialogOpen}
        onClose={closePageNoteDialog}
        pageNote={noteText}
        isAdmin={true}
        eventHandle={eventHandle}
      />
    </>
  );
};
